<template>
  <div class="quote-followup-list">
    <!-- Quick Actions Bar -->
    <div class="actions-bar mb-4">
      <div class="filter-buttons">
        <b-button-group>
          <b-button 
            :variant="activeFilter === 'overdue' ? 'danger' : 'outline-danger'"
            @click="setFilter('overdue')"
            size="sm"
          >
            🚨 Overdue ({{ overdueCount }})
          </b-button>
          <b-button 
            :variant="activeFilter === 'today' ? 'warning' : 'outline-warning'"
            @click="setFilter('today')"
            size="sm"
          >
            ⏰ Due Today ({{ todayCount }})
          </b-button>
          <b-button 
            :variant="activeFilter === 'all' ? 'primary' : 'outline-primary'"
            @click="setFilter('all')"
            size="sm"
          >
            📋 All Follow-ups
          </b-button>
        </b-button-group>
      </div>
      
      <div class="priority-filter">
        <b-form-select
          v-model="priorityFilter"
          :options="priorityOptions"
          size="sm"
          @change="loadQuotes"
        />
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <circle-loader :size="40"></circle-loader>
    </div>

    <!-- Quotes Table -->
    <div v-else-if="quotes.length" class="quotes-table">
      <b-table
        :items="quotes"
        :fields="tableFields"
        striped
        hover
        responsive
        @row-clicked="showFollowUpModal"
        tbody-tr-class="clickable-row"
      >
        <template #cell(quote)="data">
          <div class="quote-info">
            <strong>{{ data.item.nickname || 'Unnamed Quote' }}</strong>
            <br>
            <small class="text-muted">#{{ data.item.shortId }}</small>
          </div>
        </template>

        <template #cell(customer)="data">
          <div v-if="data.item.customer" class="customer-info">
            <strong>{{ data.item.customer.firstName }} {{ data.item.customer.lastName }}</strong>
            <br>
            <div class="contact-links">
              <a 
                v-if="data.item.customer.email" 
                :href="'mailto:' + data.item.customer.email"
                class="contact-link me-2"
                @click.stop
              >
                📧
              </a>
              <a 
                v-if="data.item.customer.phone" 
                :href="'tel:' + data.item.customer.phone"
                class="contact-link"
                @click.stop
              >
                📞
              </a>
            </div>
          </div>
          <span v-else class="text-muted">No customer</span>
        </template>

        <template #cell(value)="data">
          <strong>${{ formatCurrency((data.item.price && data.item.price.kit && data.item.price.kit.total) || 0) }}</strong>
        </template>

        <template #cell(type)="data">
          {{ data.item.type || 'Unknown' }}
        </template>

        <template #cell(priority)="data">
          <b-badge :variant="getPriorityVariant(data.item.followUpPriority)">
            {{ data.item.followUpPriority || 'MEDIUM' }}
          </b-badge>
        </template>

        <template #cell(nextFollowUp)="data">
          <span v-if="data.item.nextFollowUpDate" :class="getFollowUpClass(data.item.nextFollowUpDate)">
            {{ formatDate(data.item.nextFollowUpDate) }}
          </span>
          <span v-else class="text-muted">Not scheduled</span>
        </template>

        <template #cell(createdDate)="data">
          <span class="created-date">{{ formatDate(data.item.createdAt) }}</span>
        </template>

        <template #cell(creator)="data" v-if="adminMode">
          <span class="creator-info">{{ getUserDisplayName(data.item.createdBy) }}</span>
        </template>

        <template #cell(lastContact)="data">
          <span v-if="data.item.lastFollowUpDate">
            {{ formatDate(data.item.lastFollowUpDate) }}
          </span>
          <span v-else class="text-muted">Never</span>
        </template>

        <template #cell(actions)="data">
          <b-button-group size="sm">
            <b-button 
              variant="success" 
              @click.stop="showFollowUpModal(data.item)"
              size="sm"
            >
              📝
            </b-button>
            <b-button 
              variant="secondary" 
              @click.stop="markNoFollowUp(data.item)"
              size="sm"
            >
              ❌
            </b-button>
          </b-button-group>
        </template>
    </b-table>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1" class="pagination-container">
      <b-pagination
        v-model="pagination.currentPage"
        :total-rows="pagination.totalCount"
        :per-page="pagination.limit"
        :limit="5"
        align="center"
        @change="onPageChange"
        class="mt-3"
      />
      <div class="pagination-info">
        Showing {{ ((pagination.currentPage - 1) * pagination.limit) + 1 }} to {{ Math.min(pagination.currentPage * pagination.limit, pagination.totalCount) }} of {{ pagination.totalCount }} quotes
      </div>
    </div>
  </div>

    <!-- Empty State -->
    <div v-else-if="!loading" class="empty-state text-center py-5">
      <div class="empty-icon">🎉</div>
      <h5>No follow-ups needed!</h5>
      <p class="text-muted">
        All your quotes are up to date.
      </p>
    </div>

    <!-- Follow-up Modal -->
    <b-modal
      id="followup-modal"
      :title="`Follow up: ${selectedQuote && (selectedQuote.nickname || selectedQuote.shortId)}`"
      size="lg"
      @hidden="resetModal"
    >
      <div v-if="selectedQuote">
        <!-- Customer Information Summary -->
        <div class="customer-info-section mb-4">
          <div class="customer-header">
            <h6 class="customer-title">
              <i class="fas fa-user-circle me-2 text-primary"></i>
              Customer Information
            </h6>
          </div>
          
          <!-- Customer Details (when customer exists) -->
          <div v-if="hasCustomerData(selectedQuote.customer)" class="customer-details">
            <div class="row">
              <div class="col-md-6">
                <div class="customer-field" v-if="selectedQuote.customer && selectedQuote.customer.name">
                  <label class="field-label">Name:</label>
                  <span class="field-value">{{ selectedQuote.customer.name }}</span>
                </div>
                
                <div class="customer-field" v-if="selectedQuote.customer && selectedQuote.customer.email">
                  <label class="field-label">Email:</label>
                  <a :href="`mailto:${selectedQuote.customer.email}`" class="field-value field-link">
                    <i class="fas fa-envelope me-1"></i>
                    {{ selectedQuote.customer.email }}
                  </a>
                </div>
                
                <div class="customer-field" v-if="selectedQuote.customer && selectedQuote.customer.phone">
                  <label class="field-label">Phone:</label>
                  <a :href="`tel:${selectedQuote.customer.phone}`" class="field-value field-link">
                    <i class="fas fa-phone me-1"></i>
                    {{ selectedQuote.customer.phone }}
                  </a>
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="customer-field" v-if="getCustomerAddress(selectedQuote.customer)">
                  <label class="field-label">Address:</label>
                  <div class="field-value">
                    <i class="fas fa-map-marker-alt me-1"></i>
                    <span v-html="getFormattedAddress(selectedQuote.customer)"></span>
                  </div>
                </div>
                
                <div class="customer-field" v-if="selectedQuote.customer && selectedQuote.customer.company">
                  <label class="field-label">Company:</label>
                  <span class="field-value">
                    <i class="fas fa-building me-1"></i>
                    {{ selectedQuote.customer.company }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- No Customer Message -->
          <div v-else class="no-customer-message">
            <div class="text-center text-muted py-3">
              <i class="fas fa-user-slash fa-2x mb-2"></i>
              <p class="mb-0">No Customer</p>
              <small>This quote was created without customer information</small>
            </div>
          </div>
          
          <!-- Quote Summary -->
          <div class="quote-summary mt-3 pt-3 border-top">
            <div class="row">
              <div class="col-md-4">
                <div class="summary-item">
                  <label class="field-label">Building Type:</label>
                  <span class="field-value">{{ selectedQuote.type || 'Not specified' }}</span>
                </div>
              </div>
              <div class="col-md-4">
                <div class="summary-item">
                  <label class="field-label">Quote Value:</label>
                  <span class="field-value text-success fw-bold">
                    ${{ formatCurrency(getQuoteValue(selectedQuote)) }}
                  </span>
                </div>
              </div>
              <div class="col-md-4">
                <div class="summary-item">
                  <label class="field-label">Quote Date:</label>
                  <span class="field-value">{{ formatDate(selectedQuote.createdAt) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <b-tabs v-model="activeModalTab" class="followup-tabs">
          <!-- Next Follow-up Tab -->
          <b-tab title="📅 Next Follow-up" active>
            <div class="tab-content-padding">
              <div class="mb-3">
                <label class="form-label">Status</label>
                <b-form-select
                  v-model="followUpForm.status"
                  :options="statusOptions"
                />
              </div>

              <div class="mb-3">
                <label class="form-label">Priority</label>
                <b-form-select
                  v-model="followUpForm.priority"
                  :options="priorityOptions.slice(1)"
                />
              </div>

              <div class="mb-3">
                <label class="form-label">Next Follow-up Date</label>
                <div class="date-selection">
                  <b-button-group class="mb-2">
                    <b-button 
                      variant="outline-primary" 
                      size="sm"
                      @click="setFollowUpDate(7)"
                    >
                      1 Week
                    </b-button>
                    <b-button 
                      variant="outline-primary" 
                      size="sm"
                      @click="setFollowUpDate(14)"
                    >
                      2 Weeks
                    </b-button>
                    <b-button 
                      variant="outline-primary" 
                      size="sm"
                      @click="setFollowUpDate(30)"
                    >
                      1 Month
                    </b-button>
                  </b-button-group>
                  <b-form-input
                    v-model="followUpForm.nextFollowUpDate"
                    type="datetime-local"
                    placeholder="Or set custom date..."
                  />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Follow-up Notes</label>
                <b-form-textarea
                  v-model="followUpForm.note"
                  rows="4"
                  placeholder="Add notes about this follow-up..."
                />
              </div>
            </div>
          </b-tab>

          <!-- Past Follow-ups Tab -->
          <b-tab title="📋 Past Follow-ups">
            <div class="tab-content-padding">
              <FollowUpTimeline :follow-ups="selectedQuote.followUps" />
            </div>
          </b-tab>
        </b-tabs>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button 
          v-if="activeModalTab === 0"
          variant="success" 
          @click="submitFollowUp"
          :disabled="submitting"
        >
          <span v-if="submitting">Saving...</span>
          <span v-else>Save Follow-up</span>
        </b-button>
      </template>
    </b-modal>

    <!-- No Follow-up Confirmation Modal -->
    <b-modal
      id="no-followup-confirm-modal"
      title="Confirm No Follow-up Needed"
      @ok="confirmNoFollowUp"
      @cancel="cancelNoFollowUp"
      ok-title="Yes, Mark as No Follow-up"
      cancel-title="Cancel"
      ok-variant="danger"
    >
      <div v-if="noFollowUpQuote">
        <p>Are you sure you want to mark this quote as <strong>"No Follow-up Needed"</strong>?</p>
        
        <div class="quote-info bg-light p-3 rounded mb-3">
          <h6 class="mb-2">{{ noFollowUpQuote.nickname || 'Untitled Quote' }}</h6>
          <div class="text-muted small">
            <div><strong>ID:</strong> {{ noFollowUpQuote.shortId }}</div>
            <div v-if="noFollowUpQuote.customer">
              <strong>Customer:</strong> {{ noFollowUpQuote.customer.name }}
            </div>
            <div><strong>Value:</strong> ${{ formatCurrency(noFollowUpQuote.totalPrice || 0) }}</div>
            <div><strong>Type:</strong> {{ noFollowUpQuote.buildingType || 'Unknown' }}</div>
          </div>
        </div>
        
        <p class="text-warning small">
          <strong>Note:</strong> This action will remove the quote from follow-up lists and cannot be easily undone.
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import CircleLoader from '@/components/CircleLoader'
import FollowUpTimeline from './FollowUpTimeline.vue'

export default {
  name: 'QuoteFollowUpList',
  components: { 
    CircleLoader,
    FollowUpTimeline
  },
  props: {
    adminMode: {
      type: Boolean,
      default: false
    },
    filterUserEmail: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      quotes: [],
      loading: false,
      submitting: false,
      activeFilter: 'overdue',
      priorityFilter: '',
      overdueCount: 0,
      todayCount: 0,
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 20,
        hasNext: false,
        hasPrev: false
      },
      selectedQuote: null,
      noFollowUpQuote: null,
      activeModalTab: 0,
      followUpForm: {
        status: '',
        priority: '',
        nextFollowUpDate: '',
        note: ''
      },
      priorityOptions: [
        { value: '', text: 'All Priorities' },
        { value: 'URGENT', text: 'Urgent' },
        { value: 'HIGH', text: 'High' },
        { value: 'MEDIUM', text: 'Medium' },
        { value: 'LOW', text: 'Low' }
      ],
      statusOptions: [
        { value: 'IN_PROGRESS', text: 'In Progress' },
        { value: 'NO_FOLLOW_UP', text: 'No Follow-up Needed' },
        { value: 'CONVERTED', text: 'Converted to Sale' },
        { value: 'LOST', text: 'Lost Opportunity' }
      ]
    }
  },
  computed: {
    tableFields() {
      const baseFields = [
        { key: 'quote', label: 'Quote', sortable: true },
        { key: 'customer', label: 'Customer', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'value', label: 'Value', sortable: true },
        { key: 'priority', label: 'Priority', sortable: true },
        { key: 'createdDate', label: 'Created Date', sortable: true },
        { key: 'nextFollowUp', label: 'Next Follow-up', sortable: true },
        { key: 'lastContact', label: 'Last Contact', sortable: true }
      ]

      // Add Creator column for admin mode
      if (this.adminMode) {
        baseFields.splice(2, 0, { key: 'creator', label: 'Creator', sortable: true })
      }

      // Add actions column at the end
      baseFields.push({ key: 'actions', label: 'Actions', class: 'text-center' })

      return baseFields
    }
  },
  async mounted() {
    await this.loadQuotes()
  },
  methods: {
    async loadQuotes() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          limit: this.pagination.limit
        }
        
        if (this.adminMode) {
          params.adminMode = 'true'
        }
        
        if (this.filterUserEmail) {
          params.userEmail = this.filterUserEmail
        }
        
        if (this.activeFilter === 'overdue') {
          params.overdue = 'true'
        } else if (this.activeFilter === 'today') {
          params.dueToday = 'true'
        }
        
        if (this.priorityFilter) {
          params.priority = this.priorityFilter
        }

        const response = await axios.get('/services/v2/quotes/follow-ups', { params })
        this.quotes = response.data.quotes
        this.pagination = response.data.pagination || this.pagination

        // Load counts for buttons
        await this.loadCounts()
      } catch (error) {
        console.error('Error loading quotes:', error)
        this.$bvToast.toast('Error loading follow-up quotes', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    async loadCounts() {
      try {
        const baseParams = {
          limit: 1000 // Get a high limit to ensure we get all quotes for accurate counts
        }
        
        if (this.adminMode) {
          baseParams.adminMode = 'true'
        }
        
        if (this.filterUserEmail) {
          baseParams.userEmail = this.filterUserEmail
        }
        
        const [overdueResponse, todayResponse] = await Promise.all([
          axios.get('/services/v2/quotes/follow-ups', { params: { ...baseParams, overdue: 'true' } }),
          axios.get('/services/v2/quotes/follow-ups', { params: { ...baseParams, dueToday: 'true' } })
        ])

        this.overdueCount = overdueResponse.data.pagination.totalCount
        this.todayCount = todayResponse.data.pagination.totalCount
      } catch (error) {
        console.error('Error loading counts:', error)
      }
    },

    setFilter(filter) {
      this.activeFilter = filter
      this.pagination.currentPage = 1 // Reset to first page
      this.loadQuotes()
    },

    onPageChange(page) {
      this.pagination.currentPage = page
      this.loadQuotes()
    },

    async showFollowUpModal(quote) {
      // Fetch fresh quote data to ensure we have the latest followUps
      try {
        const response = await axios.get(`/services/v2/quotes/${quote.shortId}`)
        this.selectedQuote = response.data.quote || quote
      } catch (error) {
        console.error('Error fetching quote details:', error)
        // Fallback to the passed quote if API fails
        this.selectedQuote = quote
      }
      
      this.followUpForm = {
        status: this.selectedQuote.quoteStatus || 'IN_PROGRESS',
        priority: this.selectedQuote.followUpPriority || 'MEDIUM',
        nextFollowUpDate: this.selectedQuote.nextFollowUpDate
          ? moment(this.selectedQuote.nextFollowUpDate).format('YYYY-MM-DDTHH:mm')
          : moment().add(7, 'days').format('YYYY-MM-DDTHH:mm'), // Default to 1 week
        note: ''
      }
      this.$bvModal.show('followup-modal')
    },

    async submitFollowUp() {
      if (!this.selectedQuote || !this.followUpForm.note.trim()) {
        this.$bvToast.toast('Please add a follow-up note', {
          title: 'Missing Information',
          variant: 'warning'
        })
        return
      }

      this.submitting = true
      try {
        await axios.put(`/services/v2/quotes/${this.selectedQuote.shortId}/follow-up`, {
          quoteStatus: this.followUpForm.status,
          followUpPriority: this.followUpForm.priority,
          nextFollowUpDate: this.followUpForm.nextFollowUpDate || null,
          note: this.followUpForm.note
        })

        this.$bvToast.toast('Follow-up updated successfully', {
          title: 'Success',
          variant: 'success'
        })

        this.$bvModal.hide('followup-modal')
        await this.loadQuotes()
        this.$emit('quote-updated')
      } catch (error) {
        console.error('Error updating follow-up:', error)
        this.$bvToast.toast('Error updating follow-up', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.submitting = false
      }
    },

    markNoFollowUp(quote) {
      // Show confirmation modal instead of immediately marking
      this.noFollowUpQuote = quote
      this.$bvModal.show('no-followup-confirm-modal')
    },

    async confirmNoFollowUp() {
      if (!this.noFollowUpQuote) return

      try {
        await axios.put(`/services/v2/quotes/${this.noFollowUpQuote.shortId}/follow-up`, {
          quoteStatus: 'NO_FOLLOW_UP',
          followUpPriority: 'LOW',
          nextFollowUpDate: null,
          note: 'Marked as no follow-up needed'
        })

        this.$bvToast.toast('Quote marked as no follow-up needed', {
          title: 'Success',
          variant: 'success'
        })

        await this.loadQuotes()
        this.$emit('quote-updated')
      } catch (error) {
        console.error('Error marking no follow-up:', error)
        this.$bvToast.toast('Error updating quote', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.noFollowUpQuote = null
      }
    },

    cancelNoFollowUp() {
      // Clear the selected quote when canceling
      this.noFollowUpQuote = null
    },

    resetModal() {
      this.selectedQuote = null
      this.activeModalTab = 0 // Reset to first tab
      this.followUpForm = {
        status: '',
        priority: '',
        nextFollowUpDate: '',
        note: ''
      }
    },

    formatDate(date) {
      if (!date) return ''
      return moment(date).format('MMM D, YYYY')
    },

    formatCurrency(value) {
      // Handle null, undefined, NaN, or non-numeric values
      if (!value || value === 0 || isNaN(value) || typeof value !== 'number') return '0'
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    },

    getQuoteValue(quote) {
      if (!quote) return 0
      
      // Try multiple possible price fields
      let value = quote.totalPrice || 
                  (quote.price && quote.price.kitAndInstall && quote.price.kitAndInstall.total) ||
                  (quote.price && quote.price.kitAndInstall) ||
                  (quote.price && quote.price.kit && quote.price.kit.total) ||
                  (quote.price && quote.price.kit) ||
                  0
      
      // Ensure we have a valid number
      if (typeof value === 'string') {
        value = parseFloat(value.replace(/[,$]/g, ''))
      }
      
      return isNaN(value) ? 0 : value
    },

    hasCustomerData(customer) {
      if (!customer) return false
      return customer.name || customer.email || customer.phone || customer.company || this.getCustomerAddress(customer)
    },

    getCustomerAddress(customer) {
      if (!customer) return false
      
      // Handle case where address might be an object
      if (typeof customer.address === 'object' && customer.address !== null) {
        return customer.address.street || customer.address.city || customer.address.state || customer.address.zip
      }
      
      return customer.address || customer.street || customer.city || customer.state || customer.zip
    },

    getFormattedAddress(customer) {
      if (!customer) return ''
      
      const parts = []
      
      // Handle case where address might be an object
      if (typeof customer.address === 'object' && customer.address !== null) {
        if (customer.address.street) parts.push(customer.address.street)
        if (customer.address.address) parts.push(customer.address.address)
        
        const cityStateZip = []
        if (customer.address.city) cityStateZip.push(customer.address.city)
        if (customer.address.state) cityStateZip.push(customer.address.state)
        if (customer.address.zip) cityStateZip.push(customer.address.zip)
        
        if (cityStateZip.length > 0) {
          parts.push(cityStateZip.join(', '))
        }
      } else {
        // Handle flat address structure
        if (customer.address && typeof customer.address === 'string') parts.push(customer.address)
        if (customer.street) parts.push(customer.street)
        
        const cityStateZip = []
        if (customer.city) cityStateZip.push(customer.city)
        if (customer.state) cityStateZip.push(customer.state)
        if (customer.zip) cityStateZip.push(customer.zip)
        
        if (cityStateZip.length > 0) {
          parts.push(cityStateZip.join(', '))
        }
      }
      
      return parts.length > 0 ? parts.join('<br>') : ''
    },

    formatDateTime(date) {
      if (!date) return ''
      return moment(date).format('MMM D, YYYY [at] h:mm A')
    },

    getUserDisplayName(email) {
      if (!email) return 'Unknown'
      const atIndex = email.indexOf('@')
      if (atIndex === -1) return email
      
      const namePart = email.substring(0, atIndex)
      // Convert "first.last" to "First Last"
      return namePart.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join(' ')
    },

    getPriorityVariant(priority) {
      const variants = {
        'URGENT': 'danger',
        'HIGH': 'warning',
        'MEDIUM': 'info',
        'LOW': 'secondary'
      }
      return variants[priority] || 'secondary'
    },

    getFollowUpClass(date) {
      if (!date) return ''
      const followUpDate = moment(date)
      const now = moment()
      
      if (followUpDate.isBefore(now)) {
        return 'text-danger font-weight-bold'
      } else if (followUpDate.isSame(now, 'day')) {
        return 'text-warning font-weight-bold'
      }
      return 'text-muted'
    },

    setFollowUpDate(days) {
      const futureDate = moment().add(days, 'days')
      this.followUpForm.nextFollowUpDate = futureDate.format('YYYY-MM-DDTHH:mm')
    },

    getStatusText(status) {
      const statusMap = {
        'IN_PROGRESS': 'In Progress',
        'NO_FOLLOW_UP': 'No Follow-up Needed',
        'CONVERTED': 'Converted to Sale',
        'LOST': 'Lost Opportunity',
        'FOLLOW_UP_NEEDED': 'Follow-up Needed',
        'FOLLOWING_UP': 'Following Up'
      }
      return statusMap[status] || status
    }
  }
}
</script>

<style lang="scss">
.quote-followup-list {
  .actions-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .clickable-row {
    cursor: pointer;
    
    &:hover {
      background-color: #f8f9fa !important;
    }
  }

  .contact-link {
    text-decoration: none;
    color: #007bff;
    
    &:hover {
      color: #0056b3;
    }
  }

  .empty-state {
    .empty-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
    }
  }

  .notes-list {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    padding: 0.75rem;

    .note-item {
      margin-bottom: 0.75rem;
      padding-bottom: 0.75rem;
      border-bottom: 1px solid #e9ecef;

      &:last-child {
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: none;
      }

      .note-date {
        display: block;
        margin-bottom: 0.25rem;
      }

      .note-text {
        margin: 0;
        font-size: 0.9rem;
        white-space: pre-wrap;
      }
    }
  }

  // Follow-up Modal Styles
  .followup-tabs {
    .tab-content-padding {
      padding: 1rem 0;
    }
  }

  .pagination-container {
    .pagination-info {
      text-align: center;
      color: #6c757d;
      font-size: 0.875rem;
      margin-top: 0.5rem;
    }
  }

  // Customer Information Section Styles
  .customer-info-section {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1.5rem;

    .customer-header {
      margin-bottom: 1rem;
      
      .customer-title {
        color: #495057;
        font-weight: 600;
        margin: 0;
        display: flex;
        align-items: center;
      }
    }

    .customer-details {
      .customer-field {
        margin-bottom: 0.75rem;
        display: flex;
        align-items: flex-start;
        
        .field-label {
          font-weight: 600;
          color: #6c757d;
          min-width: 80px;
          margin-right: 0.5rem;
          margin-bottom: 0;
          font-size: 0.875rem;
        }
        
        .field-value {
          color: #495057;
          font-size: 0.875rem;
          flex: 1;
          
          &.field-link {
            color: #007bff;
            text-decoration: none;
            
            &:hover {
              color: #0056b3;
              text-decoration: underline;
            }
          }
        }
      }

      .quote-summary {
        background: #ffffff;
        border-radius: 6px;
        padding: 1rem;
        
        .summary-item {
          display: flex;
          align-items: center;
          margin-bottom: 0.5rem;
          
          .field-label {
            font-weight: 600;
            color: #6c757d;
            min-width: 100px;
            margin-right: 0.5rem;
            margin-bottom: 0;
            font-size: 0.875rem;
          }
          
          .field-value {
            color: #495057;
            font-size: 0.875rem;
            
            &.text-success {
              color: #28a745 !important;
            }
          }
        }
      }
    }

    .no-customer-message {
      .text-muted {
        color: #6c757d !important;
      }
      
      i {
        color: #adb5bd;
      }
      
      p {
        font-weight: 500;
        font-size: 1rem;
      }
      
      small {
        font-size: 0.875rem;
      }
    }
  }

  // New Column Styles
  .created-date {
    color: #6c757d;
    font-size: 0.875rem;
  }

  .creator-info {
    color: #495057;
    font-weight: 500;
    font-size: 0.875rem;
  }

}
</style>